import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminFeedbackQuery } from '../../../features/admin/hooks/use-admin-api'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/admin/feedback/')({
  component: AdminFeedbackPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type FeedbackItem = {
  id: string
  message: string
  pageUrl: string | null
  rating: number | null
  user: { id: string; name: string | null; email: string } | null
  createdAt: string
}

const columns: TableColumn<FeedbackItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (item) => <span className="font-mono text-xs">{item.id.slice(0, 8)}</span>,
  },
  {
    header: 'Message',
    render: (item) => (
      <CellPrimary className="max-w-md break-words whitespace-pre-line">{item.message}</CellPrimary>
    ),
  },
  {
    header: 'Rating',
    width: '70px',
    render: (item) =>
      item.rating ? (
        <span className="text-sm font-medium tabular-nums text-amber-400">{item.rating}/5</span>
      ) : (
        <CellText>{'\u2014'}</CellText>
      ),
  },
  {
    header: 'Page',
    width: '200px',
    render: (item) => {
      if (!item.pageUrl) return <CellText>{'\u2014'}</CellText>
      try {
        return (
          <CellText className="max-w-[200px] break-all">{new URL(item.pageUrl).pathname}</CellText>
        )
      } catch {
        return <CellText className="max-w-[200px] break-all">{item.pageUrl}</CellText>
      }
    },
  },
  {
    header: 'User',
    width: '200px',
    render: (item) =>
      item.user ? (
        <Link
          to="/admin/users/$id"
          params={{ id: item.user.id }}
          className="text-sm text-primary hover:underline"
        >
          {item.user.name || item.user.email}
        </Link>
      ) : (
        <CellText>{'\u2014'}</CellText>
      ),
  },
  {
    header: 'Created',
    width: '200px',
    render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
  },
]

function AdminFeedbackPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const feedbackQuery = useAdminFeedbackQuery({
    page,
    limit: LIMIT,
    search: search || undefined,
  })

  const items = feedbackQuery.data?.data?.items ?? []
  const total = feedbackQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <SearchInput
        placeholder="Search feedback..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={items}
        isLoading={feedbackQuery.isLoading}
        emptyMessage="No feedback found."
        keyExtractor={(item) => item.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
